import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { TrendingUp, BookOpen, Clock, Flag, ChevronDown, Target } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useAuth } from "@/hooks/useAuth";
import { fetchSubjects } from "@/lib/api/subjects";
import { getSubjectMetadata, type SubjectColor, type SubjectMetadata } from "@/lib/subject-metadata";
import { getMySubjects } from "@/lib/my-subjects";
import { fetchPracticeHistoryForUser, type PracticeSessionRow, listFlaggedQuestionIds } from "@/lib/api/practice";
import { fetchPersonalizationSummary, type SubjectPersonalizationSummary } from "@/lib/api/personalization";

const CATEGORY_LABELS: Record<SubjectColor, string> = {
  history: "History",
  science: "Science",
  math: "Mathematics",
  social: "Social Studies",
  economics: "Economics",
};

const COLOR_BADGE_CLASS: Record<SubjectColor, string> = {
  history: "bg-subjects-history/10 text-subjects-history",
  science: "bg-subjects-science/10 text-subjects-science",
  math: "bg-subjects-math/10 text-subjects-math",
  social: "bg-subjects-social/10 text-subjects-social",
  economics: "bg-subjects-economics/10 text-subjects-economics",
};

const COLOR_ICON_BG: Record<SubjectColor, string> = {
  history: "bg-subjects-history",
  science: "bg-subjects-science",
  math: "bg-subjects-math",
  social: "bg-subjects-social",
  economics: "bg-subjects-economics",
};

type SubjectRow = Awaited<ReturnType<typeof fetchSubjects>>[number];

function resolveColor(subject: SubjectRow | null, slug: string): SubjectColor {
  if (!subject) {
    return getSubjectMetadata(slug).color;
  }
  const candidate = (subject.category ?? "") as SubjectColor;
  if (candidate && candidate in CATEGORY_LABELS) {
    return candidate;
  }
  return getSubjectMetadata(slug).color;
}

type SubjectLookupEntry = {
  name: string;
  slug: string;
  metadata: SubjectMetadata;
  row: SubjectRow | null;
};

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();

  const subjectsQuery = useQuery({
    queryKey: ["subjects"],
    queryFn: fetchSubjects,
  });

  const mySubjectsQuery = useQuery({
    queryKey: ["my-subjects", user?.id],
    queryFn: () => getMySubjects(user!.id),
    enabled: Boolean(user?.id),
  });

  const practiceHistoryQuery = useQuery({
    queryKey: ["practice-history-user", user?.id],
    queryFn: () => fetchPracticeHistoryForUser(user!.id, 30),
    enabled: Boolean(user?.id),
  });

  const flaggedCountQuery = useQuery({
    queryKey: ["flagged-count", user?.id],
    queryFn: async () => (await listFlaggedQuestionIds(user!.id)).length,
    enabled: Boolean(user?.id),
  });

  const subjects = subjectsQuery.data ?? [];
  
  // Get subject IDs for personalization query
  const mySubjectSlugs = mySubjectsQuery.data ?? [];
  const mySubjectIds = useMemo(() => {
    return mySubjectSlugs
      .map(slug => subjects.find(s => s.slug === slug)?.id as string | undefined)
      .filter((id): id is string => Boolean(id));
  }, [mySubjectSlugs, subjects]);

  const personalizationQuery = useQuery({
    queryKey: ["personalization-summary", user?.id, mySubjectIds],
    queryFn: () => fetchPersonalizationSummary(user!.id, mySubjectIds),
    enabled: Boolean(user?.id) && mySubjectIds.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });

  const personalizationData = personalizationQuery.data ?? new Map<string, SubjectPersonalizationSummary>();

  const subjectIdMap = useMemo(() => {
    const map = new Map<string, SubjectRow>();
    subjects.forEach((subject) => {
      const id = subject.id as string | undefined;
      if (id) {
        map.set(id, subject);
      }
    });
    return map;
  }, [subjects]);

  const slugMap = useMemo(() => {
    const map = new Map<string, SubjectRow>();
    subjects.forEach((subject) => {
      const slug = (subject.slug ?? subject.id) as string | undefined;
      if (slug) {
        map.set(slug, subject);
      }
    });
    return map;
  }, [subjects]);

  const practiceSessions = practiceHistoryQuery.data ?? [];
  const flaggedTotal = flaggedCountQuery.data ?? 0;

  const subjectLookup = useMemo(() => {
    const entries = new Map<string, SubjectLookupEntry>();
    slugMap.forEach((row, slug) => {
      const baseMetadata = getSubjectMetadata(slug);
      const actualColor = resolveColor(row, slug);
      entries.set(slug, {
        name: (row.name ?? slug) as string,
        slug,
        metadata: { ...baseMetadata, color: actualColor },
        row,
      });
    });
    // Ensure we have metadata for slugs even if not returned by query yet
    mySubjectSlugs.forEach((slug) => {
      if (!entries.has(slug)) {
        const baseMetadata = getSubjectMetadata(slug);
        const actualColor = resolveColor(null, slug);
        entries.set(slug, {
          name: slug,
          slug,
          metadata: { ...baseMetadata, color: actualColor },
          row: null,
        });
      }
    });
    return entries;
  }, [slugMap, mySubjectSlugs]);

  const practiceBySlug = useMemo(() => {
    const map = new Map<string, PracticeSessionRow[]>();
    practiceSessions.forEach((session) => {
      const subjectRow = subjectIdMap.get(session.subject_id as string);
      const slug = subjectRow?.slug ?? (subjectRow?.id as string) ?? "";
      if (!slug) return;
      const arr = map.get(slug) ?? [];
      arr.push(session);
      map.set(slug, arr);
    });
    return map;
  }, [practiceSessions, subjectIdMap]);

  const activeSubjects = mySubjectSlugs.map((slug) => subjectLookup.get(slug)).filter(Boolean) as SubjectLookupEntry[];

  const totalPracticeSessions = practiceSessions.length;

  const sessionsBySubject = useMemo(() => {
    const arr = Array.from(practiceBySlug.entries()).map(([slug, sessions]) => {
      const info = subjectLookup.get(slug);
      const sorted = [...sessions].sort((a, b) => {
        const ta = new Date((a.completed_at || a.started_at) ?? 0).getTime();
        const tb = new Date((b.completed_at || b.started_at) ?? 0).getTime();
        return tb - ta;
      });
      return {
        slug,
        name: info?.name ?? slug,
        color: info?.metadata.color,
        iconComponent: info?.metadata.iconComponent as React.ComponentType<{ className?: string }> | undefined,
        sessions: sorted.map((s) => ({
          id: s.id,
          when: s.completed_at || s.started_at,
          score: s.score != null ? Math.round(Number(s.score)) : null,
          requested: ((s.config as any)?.count as number | undefined) ?? null,
        })),
      };
    }).filter((x) => x.sessions.length > 0);
    // Sort subjects by most recent activity
    arr.sort((a, b) => {
      const da = a.sessions[0]?.when ? new Date(a.sessions[0].when as string).getTime() : 0;
      const db = b.sessions[0]?.when ? new Date(b.sessions[0].when as string).getTime() : 0;
      return db - da;
    });
    return arr;
  }, [practiceBySlug, subjectLookup]);

  const [activityOpen, setActivityOpen] = useState(false);
  const [openSubject, setOpenSubject] = useState<string | null>(null);

  const loading = authLoading || subjectsQuery.isLoading;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col gap-2">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Track your subjects, practice history, and flagged questions in one place.
          </p>
        </div>

        {!user && !authLoading && (
          <Card className="mb-8 border-dashed">
            <CardContent className="py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Sign in to unlock your dashboard</h2>
                <p className="text-sm text-muted-foreground">
                  Create an account or sign in to sync subjects and practice history across devices.
                </p>
              </div>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-32 rounded-lg border animate-pulse bg-muted/40" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={<BookOpen className="h-5 w-5" />}
              label="Active Subjects"
              value={mySubjectSlugs.length}
              tone="indigo"
            />
            <StatCard
              icon={<Clock className="h-5 w-5" />}
              label="Practice Sessions"
              value={totalPracticeSessions}
              tone="violet"
            />
            <StatCard
              icon={<Flag className="h-5 w-5" />}
              label="Flagged Questions"
              value={flaggedTotal}
              supporting={flaggedCountQuery.isFetching ? "Updating…" : undefined}
              tone="rose"
            />
            <StatCard
              icon={<Target className="h-5 w-5" />}
              label="Personalized Plans"
              value={Array.from(personalizationData.values()).filter(p => p.targetGrade !== null).length}
              tone="emerald"
            />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <Card className="border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl">My subjects</CardTitle>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/subjects">Manage subjects</Link>
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {activeSubjects.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b bg-muted/50">
                        <tr className="text-left text-sm">
                          <th className="p-4">Subject</th>
                          <th className="p-4 text-center">Predicted</th>
                          <th className="p-4 text-center">Target</th>
                          <th className="p-4 text-center">Sessions</th>
                          <th className="p-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeSubjects.map((entry) => {
                          const { slug, name, metadata, row } = entry;
                          const color = metadata.color;
                          const subjectId = row?.id as string | undefined;
                          const personalization = subjectId ? personalizationData.get(subjectId) : undefined;
                          const sessions = practiceBySlug.get(slug) ?? [];
                          const lastSession = sessions[0];
                          const lastWhen = lastSession?.completed_at || lastSession?.started_at;

                          return (
                            <tr key={slug} className="border-b last:border-b-0">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-lg text-white ${COLOR_ICON_BG[color]}`}>
                                    <metadata.iconComponent className="h-6 w-6" />
                                  </div>
                                  <div>
                                    <div className="font-medium text-sm md:text-base">{name}</div>
                                    <Badge className={`mt-1 text-xs ${COLOR_BADGE_CLASS[color]}`}>
                                      {color.charAt(0).toUpperCase() + color.slice(1)}
                                    </Badge>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4 text-center">
                                {personalization?.predictedGrade ? (
                                  <span className="font-bold text-lg">{personalization.predictedGrade}</span>
                                ) : (
                                  <span className="text-muted-foreground text-xs">—</span>
                                )}
                              </td>
                              <td className="p-4 text-center">
                                {personalization?.targetGrade ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="font-medium">{personalization.targetGrade}</span>
                                  </div>
                                ) : (
                                  <Link 
                                    to={`/subjects/${slug}/personalized`} 
                                    className="text-xs text-primary hover:underline"
                                  >
                                    Set goal
                                  </Link>
                                )}
                              </td>
                              <td className="p-4 text-center text-sm">
                                {sessions.length ? (
                                  <div className="space-y-1">
                                    <div className="font-medium">{sessions.length}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {lastWhen ? new Date(lastWhen).toLocaleDateString() : ""}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs">0</span>
                                )}
                              </td>
                              <td className="p-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button variant="ghost" size="sm" asChild>
                                    <Link to={`/flagged/${slug}`} aria-label={`Flagged questions for ${name}`}>
                                      <Flag className="h-4 w-4" />
                                    </Link>
                                  </Button>
                                  <Button variant="ghost" size="sm" asChild>
                                    <Link to={`/subjects/${slug}`} aria-label={`Open ${name}`}>
                                      →
                                    </Link>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    Add a subject to see it tracked here. Browse all options in the subjects catalog.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {!activityOpen ? (
            <div className="border rounded-lg self-start inline-block w-full min-h-0 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-1.5">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  <span className="text-xl font-semibold">Recent activity</span>
                </div>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition"
                  aria-expanded={activityOpen}
                  onClick={() => setActivityOpen(true)}
                  aria-label="Expand recent activity"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <Card className="border self-start h-auto min-h-0 overflow-hidden">
              <CardHeader className="p-0">
                <div className="flex flex-row items-center justify-between px-4 py-1.5">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    <CardTitle className="text-xl">Recent activity</CardTitle>
                  </div>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition"
                    aria-expanded={activityOpen}
                    onClick={() => setActivityOpen(false)}
                    aria-label="Collapse recent activity"
                  >
                    <ChevronDown className="h-4 w-4 rotate-180" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {sessionsBySubject.length ? (
                  <div>
                    {sessionsBySubject.map((subject, idx) => {
                      const Icon = subject.iconComponent ?? BookOpen;
                      const color = subject.color;
                      const isOpen = openSubject === subject.slug;
                      return (
                        <div key={subject.slug} className={idx === 0 ? "" : "border-t"}>
                          <button
                            type="button"
                            className="w-full px-4 py-2.5 text-left hover:bg-muted/50"
                            onClick={() => setOpenSubject((s) => (s === subject.slug ? null : subject.slug))}
                            aria-expanded={isOpen}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg text-white ${color ? COLOR_ICON_BG[color] : "bg-muted"}`}>
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{subject.name}</span>
                                  <Badge className={`text-xs ${color ? COLOR_BADGE_CLASS[color] : ""}`}>
                                    {subject.sessions.length} test{subject.sessions.length === 1 ? "" : "s"}
                                  </Badge>
                                </div>
                              </div>
                              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </div>
                          </button>
                          {isOpen && (
                            <div>
                              {subject.sessions.map((s, sIdx) => (
                                <Link
                                  key={s.id}
                                  to={`/practice/${subject.slug}/summary?id=${encodeURIComponent(s.id as string)}`}
                                  className={`block px-4 py-2 hover:bg-muted/50 ${sIdx > 0 ? "border-t" : ""}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm">
                                      <div className="font-medium">Test #{subject.sessions.length - sIdx}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {s.when ? new Date(s.when as string).toLocaleString() : "In progress"}
                                      </div>
                                    </div>
                                    {s.score != null && (
                                      <div className="text-sm font-semibold text-foreground">{s.score}%</div>
                                    )}
                                  </div>
                                  <div className="mt-1">
                                    <ScoreBar sessionId={s.id as string} requested={s.requested ?? null} />
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm text-muted-foreground">
                    No practice sessions yet. Start a set to build momentum.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

type StatCardProps = {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  supporting?: string;
  tone?: "indigo" | "violet" | "emerald" | "rose";
};

function StatCard({ icon, label, value, supporting, tone = "indigo" }: StatCardProps) {
  const toneStyles: Record<
    NonNullable<StatCardProps["tone"]>,
    { card: string; iconBg: string; label: string; value: string; supporting: string; decor: string }
  > = {
    indigo: {
      card: "bg-indigo-50 border-indigo-200",
      iconBg: "bg-indigo-600 text-white",
      label: "text-indigo-700",
      value: "text-indigo-900",
      supporting: "text-indigo-700/80",
      decor: "bg-indigo-300",
    },
    violet: {
      card: "bg-violet-50 border-violet-200",
      iconBg: "bg-violet-600 text-white",
      label: "text-violet-700",
      value: "text-violet-900",
      supporting: "text-violet-700/80",
      decor: "bg-violet-300",
    },
    emerald: {
      card: "bg-emerald-50 border-emerald-200",
      iconBg: "bg-emerald-600 text-white",
      label: "text-emerald-700",
      value: "text-emerald-900",
      supporting: "text-emerald-700/80",
      decor: "bg-emerald-300",
    },
    rose: {
      card: "bg-rose-50 border-rose-200",
      iconBg: "bg-rose-600 text-white",
      label: "text-rose-700",
      value: "text-rose-900",
      supporting: "text-rose-700/80",
      decor: "bg-rose-300",
    },
  };

  const s = toneStyles[tone];

  return (
    <Card className={`relative overflow-hidden border ${s.card}`}>
      <div className={`pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-20 ${s.decor}`} />
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center shadow-sm ${s.iconBg}`}>
            {icon}
          </div>
          <div className="flex-1">
            <div className={`text-xs font-medium tracking-wide ${s.label}`}>{label}</div>
            <div className={`mt-1 text-2xl font-extrabold ${s.value}`}>{value}</div>
            {supporting && <div className={`text-xs mt-1 ${s.supporting}`}>{supporting}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreBar({ sessionId, requested }: { sessionId: string; requested?: number | null }) {
  const summaryQuery = useQuery({
    queryKey: ["practice-summary-mini", sessionId],
    queryFn: () => fetchPracticeSummary(sessionId),
    enabled: Boolean(sessionId),
    staleTime: 60_000,
  });
  const attempts = (summaryQuery.data?.attempts as Array<{ is_correct: boolean | null }> | undefined) ?? [];
  const totalRaw = attempts.length;
  const total = totalRaw || Math.max(requested ?? 0, 0);
  const correct = attempts.filter((a) => a.is_correct === true).length;
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;

  if (summaryQuery.isLoading) {
    return <div className="h-5 w-full rounded-full bg-muted animate-pulse" />;
  }

  return (
    <div className="relative h-5 w-full rounded-full overflow-hidden" aria-label={`Score ${correct} out of ${total}`}>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, #FACC15 0%, #FDE68A 35%, #D9F99D 65%, #22C55E 100%)",
        }}
      />
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-foreground/70"
        style={{ left: `calc(${pct}% - 1px)` }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-semibold text-foreground">{correct}/{total || 0}</span>
      </div>
    </div>
  );
}